/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import {
	createEventEmitter,
	emitEvent,
	offEvent,
	onEvent,
	removeAllEventListeners,
	TSimpleEventEmitter
} from "@adapptio/lib-shared";
import { IDisposableDependency } from "@adapptio/lib-core";

import { BrowserLogger } from "../common/BrowserLogger";
import { IView, IViewConstructor, TViewComponent } from "./IView";
import { StateService } from "../state/StateService";
import { IEditor } from "../IEditor";
import { DialogService } from "../dialogs/DialogService";
import { ConfirmationDialog } from "../dialogs/ConfirmationDialog/ConfirmationDialog";
import { termsEditorCommon } from "../terms/common";
import { isViewEditable } from "./viewUtils";
import { dataEqual } from "@adapptio/lib-blueprint";
import { ACTION_BUTTON_STYLE } from "../ui/components/ActionButton";

interface IViewRegistry {
	[K: string]: {
		constructor: IViewConstructor;
		component: TViewComponent<IView>;
	};
}

export enum VIEW_STATE {
	LOADING = "LOADING",
	READY = "READY",
	ERROR = "ERROR",
	DISPOSED = "DISPOSED"
}

export interface IViewInstance {
	name: string;
	id: string;
	params: unknown;
	viewState?: unknown;
	view: IView;
	state: VIEW_STATE;
	error?: unknown;
	onStateChange: TSimpleEventEmitter<void>;
	onClose: TSimpleEventEmitter<void>;
}

type TOpenedViewsState = Array<{
	name: string;
	id: string;
	params: unknown;
	state?: unknown;
}>;

/**
 * Service options
 */
export interface IViewServiceOpts {}

/**
 * Service to manage views (editor windows)
 */
export class ViewService implements IDisposableDependency {
	private static STATE_KEY__OPENED_VIEWS = "viewService.openedViews";
	private static STATE_KEY__CURRENT_VIEW_ID = "viewService.currentViewId";

	/** Logger facility */
	private logger: BrowserLogger;

	/** State service instance */
	private stateSvc: StateService;

	/** Dialog service instance */
	private dialogSvc: DialogService;

	/** Registered views */
	private viewRegistry: IViewRegistry = {};

	/** Opened view instances */
	private viewInstances: Array<IViewInstance> = [];

	/** Index of viewId => viewInstance */
	private viewInstanceIndex: { [K: string]: IViewInstance } = {};

	/** ID of the currently selected view */
	private currentViewId: string = null;

	/** Emitted when a view is opened or closed */
	public onViewListChanged = createEventEmitter<void>();

	/** Emitted when current view change */
	public onCurrentViewChanged = createEventEmitter<void>();

	/** Emitted when any of views changed its info */
	public onViewInfoChanged = createEventEmitter<void>();

	/** Saving views */
	private saveAllInProgress = false;

	/** Emitted when saving all views status changes */
	public onSavingChange = createEventEmitter<void>();

	private emitViewInfoChange: () => void;

	/**
	 * Resource Service Constructor
	 *
	 * @param dc Dependency Container
	 * @param opts Configuration options
	 */
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	public constructor(private dc: IEditor, opts: IViewServiceOpts) {
		this.logger = dc.get("logger").facility("ViewService");
		this.stateSvc = dc.get("stateService");
		this.dialogSvc = dc.get("dialogService");

		this.stateSvc.register(ViewService.STATE_KEY__OPENED_VIEWS, []);
		this.stateSvc.register(ViewService.STATE_KEY__CURRENT_VIEW_ID, null);

		this.emitViewInfoChange = () => emitEvent(this.onViewInfoChanged);

		window?.addEventListener("beforeunload", this.handleBrowserClose);
	}

	private handleBrowserClose = (event: BeforeUnloadEvent) => {
		if (this.hasModifiedViews()) {
			event.preventDefault();
		}
	};

	/**
	 * Registers view
	 *
	 * @param viewName View name
	 * @param viewConstructor View instnace constructor
	 */
	public registerView<TViewCtor extends IViewConstructor>(
		viewName: string,
		viewConstructor: TViewCtor,
		component: TViewComponent<InstanceType<TViewCtor>>
	): void {
		if (this.viewRegistry[viewName]) {
			throw new Error(`View '${viewName}' is already registered.`);
		}

		this.viewRegistry[viewName] = {
			constructor: viewConstructor,
			component: component
		};

		this.logger.debug(`View '${viewName}' registered.`);
	}

	/**
	 * Opens a view
	 *
	 * If ID is not set it will by generated (default behaviour, normally you don't have to set IDs).
	 * If a view with provided ID is already opened it will be focused.
	 *
	 * Set `emitChange` to false only if you are opening multiple views at once. Then you MUST emit change event yourself.
	 *
	 * @param viewName View name
	 * @param params Params
	 * @param id ID
	 * @param selectView If to select opened view
	 * @param emitChange Internal: If to emit change event
	 * @param viewState Optional: Saved view state to restore
	 */
	public open(
		viewName: string,
		id: string,
		params: unknown,
		selectView = true,
		emitChange = true,
		viewState?: unknown
	): IViewInstance {
		const sameOpenedView = this.viewInstanceIndex[id];

		// Check if already opened
		if (sameOpenedView) {
			if (selectView) {
				this.select(id);
			}

			if (dataEqual(sameOpenedView.params, params)) {
				return sameOpenedView;
			}

			sameOpenedView.params = params;
		}

		if (!sameOpenedView) {
			// Try to get view definition
			const viewDef = this.viewRegistry[viewName];

			if (!viewDef) {
				throw new Error(`Unknown view '${viewName}'.`);
			}

			// Create instance
			const viewInstance: IViewInstance = {
				name: viewName,
				id: id,
				params: params,
				viewState: viewState,
				view: new viewDef.constructor(this.dc),
				state: VIEW_STATE.LOADING,
				error: null,
				onStateChange: createEventEmitter(),
				onClose: createEventEmitter()
			};

			this.viewInstances.push(viewInstance);
			this.viewInstanceIndex[id] = viewInstance;

			if (emitChange) {
				emitEvent(this.onViewListChanged);
			}

			// Select
			if (selectView || this.viewInstances.length === 1) {
				this.select(id);
			}
		}

		const viewInstance = this.viewInstanceIndex[id];

		// Init
		viewInstance.view
			.init(params, id, viewState)
			.then(() => {
				viewInstance.state = VIEW_STATE.READY;
				emitEvent(viewInstance.onStateChange);
			})
			.catch((err) => {
				this.logger.warn("Failed to init view:", viewInstance);

				viewInstance.state = VIEW_STATE.ERROR;
				viewInstance.error = err;
				emitEvent(viewInstance.onStateChange);
			});

		if (viewInstance.view.onViewInfoChanged) {
			onEvent(viewInstance.view.onViewInfoChanged, this.emitViewInfoChange);
		}

		// Subscribe to view state changes
		if (viewInstance.view.onViewStateChanged) {
			onEvent(viewInstance.view.onViewStateChanged, () => this.handleViewStateChanged(viewInstance));
		}

		this.storeState();
		this.logger.debug(`View '${id}' opened.`, id, viewInstance);

		return viewInstance;
	}

	/**
	 * Selects (focuses) an opened view
	 *
	 * @param viewId View ID
	 * @returns
	 */
	public select(viewId: string): void {
		if (this.currentViewId !== viewId) {
			if (viewId !== null && !this.viewInstanceIndex[viewId]) {
				this.logger.warn(`Cannot select view ID '${viewId}' because it does not exist.`);
				return;
			}

			this.currentViewId = viewId;
			this.stateSvc.set(ViewService.STATE_KEY__CURRENT_VIEW_ID, viewId);

			emitEvent(this.onCurrentViewChanged);
		}
	}

	/**
	 * Sorts views
	 * @param sourceViewId Source view ID
	 * @param targetViewId Target view ID
	 * @param offset Offset (-1 or 1)
	 * @returns 
	 */
	public sort(sourceViewId: string, targetViewId: string, offset: number): void {
		// If offset <= 0, move the source view before the target view, otherwise move it after the target view
		const sourceViewIndex = this.viewInstances.findIndex((viewInstance) => viewInstance.id === sourceViewId);
		const [ sourceView ] = this.viewInstances.splice(sourceViewIndex, 1);

		const targetViewIndex = this.viewInstances.findIndex((viewInstance) => viewInstance.id === targetViewId);

		this.viewInstances.splice(targetViewIndex + (offset > 0 ? 1 : 0), 0, sourceView);

		this.storeState();
		emitEvent(this.onViewListChanged);
	}

	/**
	 * Closes view and returns if view was closed successfully.
	 *
	 * If view does not exists null will be returned.
	 *
	 * @param viewId View ID
	 */
	public async close(viewId: string, { force }: { force?: boolean } = {}): Promise<boolean> {
		if (!this.viewInstanceIndex[viewId]) {
			this.logger.warn(`Cannot close view ID '${viewId}' because it does not exist.`);
			return null;
		}

		// Get instance
		const viewInstance = this.viewInstanceIndex[viewId];
		const viewInfo = viewInstance.view.getViewInfo();

		if (!force && !viewInfo.canClose) {
			this.logger.debug(`Cannot close view ID '${viewId}': prevented by view instance.`);
			return false;
		}

		if (!force && viewInfo.isModified) {
			try {
				const confirmed = await this.confirmClose({ viewLabel: viewInfo.label });

				if (!confirmed) {
					return false;
				}
			} catch (err) {
				return false;
			}
		}

		// Dispose
		viewInstance.state = VIEW_STATE.DISPOSED;
		viewInstance.view.dispose();
		emitEvent(viewInstance.onClose);

		if (viewInstance.view.onViewInfoChanged) {
			offEvent(viewInstance.view.onViewInfoChanged, this.emitViewInfoChange);
		}

		// Note: onViewStateChanged handler is cleaned up automatically since we use inline arrow function

		// Remove
		const i = this.viewInstances.indexOf(viewInstance);

		if (i >= 0) {
			this.viewInstances.splice(i, 1);
		}

		delete this.viewInstanceIndex[viewId];

		// Store state
		this.storeState();

		emitEvent(this.onViewListChanged);

		// Select another view if current one was selected
		if (this.currentViewId === viewId) {
			const selectIndex = Math.min(i, this.viewInstances.length - 1);

			if (this.viewInstances[selectIndex]) {
				this.select(this.viewInstances[selectIndex].id);
			} else {
				this.select(null);
			}
		}

		this.logger.debug(`View '${viewId}' has been closed.`, viewId);
		return true;
	}

	/**
	 *  Ask user to confirm view close
	 */
	private async confirmClose({ viewLabel }: { viewLabel: string }): Promise<boolean> {
		return new Promise((resolve, reject) => {
			this.dialogSvc
				.open(ConfirmationDialog, "confirm-close", {
					title: termsEditorCommon.dialog.closeModifiedView.title,
					message: termsEditorCommon.dialog.closeModifiedView.message,
					translateVars: {
						name: viewLabel
					},
					icon: "mdi/alert",
					confirmLabel: termsEditorCommon.dialog.closeModifiedView.confirmButton,
					confirmIcon: "mdi/delete",
					confirmStyle: ACTION_BUTTON_STYLE.DANGER,
					doConfirm: async () => {
						resolve(true);
					}
				})
				.then(() => {
					reject();
				})
				.catch((err) => {
					reject(err);
				});
		});
	}

	/**
	 * Closes and re-opens existing view
	 *
	 * @param viewId View ID
	 */
	public reload(viewId: string): void {
		if (!this.viewInstanceIndex[viewId]) {
			this.logger.warn(`Cannot reload view ID '${viewId}' because it does not exist.`);
			return null;
		}

		const viewInstance = this.viewInstanceIndex[viewId];
		const viewDef = this.viewRegistry[viewInstance.name];

		viewInstance.view.dispose();

		viewInstance.state = VIEW_STATE.LOADING;
		viewInstance.error = null;

		viewInstance.view = new viewDef.constructor(this.dc);

		emitEvent(this.onViewListChanged);
		emitEvent(viewInstance.onStateChange);

		// Init
		viewInstance.view
			.init(viewInstance.params, viewId)
			.then(() => {
				viewInstance.state = VIEW_STATE.READY;
				emitEvent(viewInstance.onStateChange);
			})
			.catch((err) => {
				this.logger.warn("Failed to init view:", viewInstance);

				viewInstance.state = VIEW_STATE.ERROR;
				viewInstance.error = err;
				emitEvent(viewInstance.onStateChange);
			});

		this.logger.debug(`View '${viewId}' has been re-loaded.`, viewId);
	}

	/**
	 * Returns a list of unsaved view instances
	 */
	public getUnsavedViewsInstances(): IViewInstance[] {
		return this.viewInstances.filter((viewInstance) => {
			const info = viewInstance.view.getViewInfo();
			return info.isModified && info.canSave && isViewEditable(viewInstance.view);
		});
	}

	/**
	 * Returns if there are any unsaved views that can be saved
	 * @returns
	 */
	public canSaveAllViews(): boolean {
		return !this.isSaveInProgress() && this.hasModifiedViews();
	}

	/**
	 * Save all views
	 */
	public async saveAllViews(): Promise<void> {
		if (this.isSaveInProgress()) {
			this.logger.info("View service is already saving all files");
			return;
		}

		try {
			this.setSaveAllInProgress(true);

			const savingPromises = this.getUnsavedViewsInstances().map((viewInstance) =>
				isViewEditable(viewInstance.view) ? viewInstance.view.save() : null
			);

			for (const promise of savingPromises) {
				await promise;
			}
		} finally {
			this.setSaveAllInProgress(false);
		}
	}

	/**
	 * Returns if save all operation is in progress
	 */
	public isSaveInProgress(): boolean {
		return this.saveAllInProgress;
	}

	/**
	 * Sets if save all operation is in progress
	 * @param saveInProgress If saving operation is being performed
	 */
	private setSaveAllInProgress(saveInProgress: boolean): void {
		this.saveAllInProgress = saveInProgress;
		emitEvent(this.onSavingChange);
	}

	/**
	 * Returns currently selected view ID
	 */
	public getCurrentViewId(): string {
		return this.currentViewId;
	}

	/**
	 * Returns currently selected view object (or null when no view is selected)
	 */
	public getCurrentView(): IView {
		if (this.viewInstanceIndex[this.currentViewId]) {
			return this.viewInstanceIndex[this.currentViewId].view;
		} else {
			return null;
		}
	}

	/**
	 * Returns currently selected view name
	 */
	public getCurrentViewName(): string {
		if (this.viewInstanceIndex[this.currentViewId]) {
			return this.viewInstanceIndex[this.currentViewId].name;
		} else {
			return null;
		}
	}

	/**
	 * Returns if a view with given NAME is active.
	 */
	public isViewActive(viewName: string): boolean {
		return this.getCurrentViewName() === viewName;
	}

	/**
	 * Returns current view instance (use with care)
	 */
	public getCurrentViewInstance(): IViewInstance {
		if (this.viewInstanceIndex[this.currentViewId]) {
			return this.viewInstanceIndex[this.currentViewId];
		} else {
			return null;
		}
	}

	/**
	 * Returns a list of view instances
	 */
	public getViewInstanceList(): IViewInstance[] {
		return this.viewInstances;
	}

	/**
	 * Returns a view object by ID (or null if does not exists)
	 *
	 * @param viewId View ID
	 */
	public getViewById(viewId: string): IView {
		if (this.viewInstanceIndex[viewId]) {
			return this.viewInstanceIndex[viewId].view;
		} else {
			return null;
		}
	}

	/**
	 * Returns a view instance by ID (or null if does not exists)
	 *
	 * @param viewId View ID
	 */
	public getViewInstanceById(viewId: string): IViewInstance {
		if (this.viewInstanceIndex[viewId]) {
			return this.viewInstanceIndex[viewId];
		} else {
			return null;
		}
	}

	/**
	 * Returns view React component by view name (not ID).
	 * Returns null when the view is not registered.
	 *
	 * @param viewName View name
	 */
	public getComponent(viewName: string): TViewComponent<IView> {
		if (this.viewRegistry[viewName]) {
			return this.viewRegistry[viewName].component;
		} else {
			return null;
		}
	}

	/**
	 * Initializes services
	 */
	public async init(): Promise<void> {
		this.restoreFromState();
	}

	/**
	 * Disposes service
	 */
	public async dispose(): Promise<void> {
		window?.removeEventListener("beforeunload", this.handleBrowserClose);

		// Dispose instances
		this.viewInstances.forEach((item) => {
			item.view.dispose();
			item.state = VIEW_STATE.DISPOSED;
		});

		removeAllEventListeners(this.onSavingChange);
		removeAllEventListeners(this.onViewInfoChanged);
		removeAllEventListeners(this.onCurrentViewChanged);
		removeAllEventListeners(this.onViewListChanged);
	}

	/**
	 * Stores opened views to state
	 */
	private storeState() {
		const state: TOpenedViewsState = this.viewInstances.map((item) => ({
			name: item.name,
			id: item.id,
			params: item.params,
			state: item.viewState
		}));

		this.stateSvc.set(ViewService.STATE_KEY__OPENED_VIEWS, state);
	}

	/**
	 * Handles view state change - updates stored state
	 */
	private handleViewStateChanged(viewInstance: IViewInstance): void {
		if (viewInstance.view.getViewState) {
			viewInstance.viewState = viewInstance.view.getViewState();
			this.storeState();
		}
	}

	/**
	 * Restores opened views from state
	 */
	private restoreFromState() {
		const openedViewState = this.stateSvc.get<TOpenedViewsState>(ViewService.STATE_KEY__OPENED_VIEWS);
		const currentViewId = this.stateSvc.get<string>(ViewService.STATE_KEY__CURRENT_VIEW_ID);

		openedViewState.forEach((item) => {
			try {
				this.open(item.name, item.id, item.params, false, false, item.state);
			} catch (err) {
				this.logger.warn(`Failed to restore view '${item.id}' as '${item.name}':`, err);
			}
		});

		emitEvent(this.onViewListChanged);

		if (currentViewId) {
			this.select(currentViewId);
		}
	}

	/**
	 * Returns true if any of registered views is modified
	 */
	public hasModifiedViews(): boolean {
		return this.viewInstances.some((viewInstance) => viewInstance.view.getViewInfo().isModified);
	}
}
